import React, { useState, useEffect } from 'react';
import {
  Table, Button, Input, Modal, message, Typography, Empty, Result,
  Card, Row, Col, Tooltip, Dropdown, Select, Segmented, Badge, Alert, Collapse, Spin,
} from 'antd';
import { SearchOutlined, ReloadOutlined, FileTextOutlined, ArrowRightOutlined, ShopOutlined, InfoCircleOutlined, CopyOutlined, ThunderboltOutlined, SaveOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getTraslados,
  getTrasladoById,
  updateTrasladoEstado,
  procesarTraslado,
  getGescomFarmacia,
  ESTADO_PENDIENTE,
  type ReconciliacionTraslado,
  type ProcesarTrasladoResponse,
  type GescomFarmaciaData,
  getRecTitularesFarmaciaGescom,
} from '../../api/reconciliacionApi';
import { fct } from '../../fct/tokens/tokens';
import { FctEstado, FctGlifoAtencion, type FctFamilia, type FctNombreIcono } from '../../fct/react';
import FarmaciaTitularesModal from '../FarmaciaTitularesModal';
import AbrirCasoButton from '../MejoraCU2/AbrirCasoButton';
import { ResultadoIcono } from './semantica';

const { Text } = Typography;
const { TextArea } = Input;

const ESTADO_TODOS = -1;

// Estados del traslado — valores no contiguos (0,1,2,3,5). familia/icono = semáforo del manual (FctEstado), badgeColor = Badge del filtro.
const ESTADOS_TRASLADO: { value: number; label: string; familia: FctFamilia; icono: FctNombreIcono; badgeColor: string }[] = [
  { value: 0, label: 'Pendiente', familia: 'aviso', icono: 'hora', badgeColor: fct.estado.aviso.linea },
  { value: 1, label: 'Traslado OK', familia: 'exito', icono: 'completado', badgeColor: fct.estado.exito.linea },
  { value: 2, label: 'Formato', familia: 'proceso', icono: 'transmision', badgeColor: fct.estado.proceso.linea },
  { value: 3, label: 'Cambio nombre', familia: 'proceso', icono: 'editar', badgeColor: fct.estado.neutro.linea },
  { value: 5, label: 'Descartado', familia: 'neutro', icono: 'cancelado', badgeColor: fct.tinta['500'] },
];

const estadoLabels: Record<number, { label: string; familia: FctFamilia; icono: FctNombreIcono }> = Object.fromEntries(
  ESTADOS_TRASLADO.map(e => [e.value, { label: e.label, familia: e.familia, icono: e.icono }])
);

const EstadoTraslado: React.FC<{ estadoId: number }> = ({ estadoId }) => {
  const est = estadoLabels[estadoId] || { label: `Estado ${estadoId}`, familia: 'neutro' as FctFamilia, icono: 'punto' as FctNombreIcono };
  return <FctEstado personalizado={{ etiqueta: est.label, familia: est.familia, icono: est.icono }} />;
};

const FmtField: React.FC<{ label: string; oldVal?: string; newVal?: string }> = ({ label, oldVal, newVal }) => {
  const changed = (oldVal || '') !== (newVal || '');
  return (
    <div style={{ marginBottom: 4 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Text style={{ flex: 1 }}>{oldVal || '-'}</Text>
        <ArrowRightOutlined style={{ fontSize: 12 }} />
        <Text strong style={{ flex: 1, backgroundColor: changed ? fct.estado.aviso.relleno : undefined, padding: changed ? '2px 6px' : undefined, borderRadius: fct.radio.micro }}>
          {newVal || '-'}
        </Text>
      </div>
    </div>
  );
};

/** Valor de la nueva dirección: si cambia respecto a la anterior, glifo de atención y negrita (sin pintar el fondo). */
const ValorNuevo: React.FC<{ cambiado: boolean; mono?: boolean; children: React.ReactNode }> = ({ cambiado, mono, children }) => (
  <div className={mono ? 'fct-mono' : undefined} style={{ padding: '2px 4px', fontWeight: cambiado ? 700 : undefined }}>
    {cambiado && <FctGlifoAtencion nivel="atencion" />}
    {children}
  </div>
);

interface TrasladosTabProps {
  selectedRecIds?: number[];
}

const TrasladosTab: React.FC<TrasladosTabProps> = ({ selectedRecIds }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<ReconciliacionTraslado[]>([]);
  const [filteredData, setFilteredData] = useState<ReconciliacionTraslado[]>([]);
  const [selectedEstado, setSelectedEstado] = useState<number>(ESTADO_PENDIENTE);
  const [searchText, setSearchText] = useState('');
  const [estadoCounts, setEstadoCounts] = useState<Record<number, number>>({});
  const [loadingCounts, setLoadingCounts] = useState(false);

  // Tratamiento por lotes (solo en vista de pendientes)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [batchEstado, setBatchEstado] = useState<number | undefined>(undefined);
  const [batchLoading, setBatchLoading] = useState(false);

  // Modal detalle
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ReconciliacionTraslado | null>(null);
  const [editNotas, setEditNotas] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [editEstadoId, setEditEstadoId] = useState<number>(ESTADO_PENDIENTE);
  const [showTitularesModal, setShowTitularesModal] = useState(false);

  // Procesamiento en Gescom (#194)
  const [procesando, setProcesando] = useState(false);
  const [procesarResult, setProcesarResult] = useState<ProcesarTrasladoResponse | null>(null);
  // Dirección actual de la farmacia en Gescom (para la cabecera del modal)
  const [gescomFarmacia, setGescomFarmacia] = useState<GescomFarmaciaData | null>(null);
  const [loadingGescom, setLoadingGescom] = useState(false);

  const fetchByEstado = async (estadoId: number) => {
    const apiEstado = estadoId === ESTADO_TODOS ? undefined : estadoId;
    if (!selectedRecIds || selectedRecIds.length === 0) return getTraslados(undefined, apiEstado);
    const results = await Promise.all(selectedRecIds.map(rid => getTraslados(rid, apiEstado)));
    return results.flat();
  };

  const loadCounts = async () => {
    setLoadingCounts(true);
    try {
      const results = await Promise.all(ESTADOS_TRASLADO.map(e => fetchByEstado(e.value)));
      const counts: Record<number, number> = {};
      ESTADOS_TRASLADO.forEach((e, i) => { counts[e.value] = results[i].length; });
      setEstadoCounts(counts);
    } catch { /* silently fail */ }
    finally { setLoadingCounts(false); }
  };

  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await fetchByEstado(selectedEstado);
      setData(result);
      applySearch(result, searchText);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedEstado, selectedRecIds]);

  useEffect(() => {
    loadCounts();
  }, [selectedRecIds]);

  const applySearch = (items: ReconciliacionTraslado[], text: string) => {
    if (!text.trim()) {
      setFilteredData(items);
      return;
    }
    const lower = text.toLowerCase();
    setFilteredData(items.filter(t =>
      (t.farmaciaGescom?._Name || '').toLowerCase().includes(lower) ||
      (t.direccion || '').toLowerCase().includes(lower) ||
      (t.localidad || '').toLowerCase().includes(lower) ||
      (t.provincia || '').toLowerCase().includes(lower)
    ));
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    applySearch(data, value);
  };

  const handleUpdateEstado = async (id: number, estadoId: number, notasGestion?: string) => {
    setActionLoading(true);
    try {
      await updateTrasladoEstado(id, estadoId, notasGestion);
      message.success('Traslado actualizado correctamente');
      setModalVisible(false);
      loadData();
      loadCounts();
    } catch {
      message.error('Error al actualizar el traslado');
    } finally {
      setActionLoading(false);
    }
  };

  const openDetail = (item: ReconciliacionTraslado) => {
    setSelectedItem(item);
    setEditNotas(item.notasGestion || '');
    setEditEstadoId(item.estadoId);
    setProcesarResult(null);
    setGescomFarmacia(null);
    setModalVisible(true);
    if (item.idFarmaciaGS) {
      setLoadingGescom(true);
      getGescomFarmacia(item.idFarmaciaGS)
        .then(setGescomFarmacia)
        .catch(() => setGescomFarmacia(null))
        .finally(() => setLoadingGescom(false));
    }
  };

  const handleProcesar = (item: ReconciliacionTraslado) => {
    Modal.confirm({
      title: 'Aplicar traslado en Gescom',
      content: `Se cambiará la calle de la farmacia en Gescom a: "${item.direccion || '(sin dirección)'}", y se vaciará su geolocalización, que es la de la calle anterior. El código postal, la población y la provincia no cambian. Esta acción no se puede deshacer. ¿Continuar?`,
      okText: 'Aceptar',
      cancelText: 'Cancelar',
      onOk: async () => {
        setProcesando(true);
        try {
          const result = await procesarTraslado(item.id);
          setProcesarResult(result);
          if (result.error !== 0) {
            message.error(`Error: ${result.mensajeError}`);
          } else {
            message.success('Traslado aplicado correctamente en Gescom');
            // Gescom está cambiado, pero CU2 no recibió el estado: el aviso dice cómo reintentarlo.
            if (result.avisoCU2) message.warning(result.avisoCU2);
            // La dirección en Gescom ha cambiado: refrescar la cabecera
            if (item.idFarmaciaGS) {
              setLoadingGescom(true);
              getGescomFarmacia(item.idFarmaciaGS)
                .then(setGescomFarmacia)
                .catch(() => { /* mantener la anterior */ })
                .finally(() => setLoadingGescom(false));
            }
          }
          // Refrescar el caso (estado, notasProcesamiento, fechaProcesamiento)
          const refreshed = await getTrasladoById(item.id);
          setSelectedItem(refreshed);
          setEditEstadoId(refreshed.estadoId);
          loadData();
          loadCounts();
        } catch (err: any) {
          // Un rechazo de Gescom (#194) llega como 409 con su motivo, sin escribir nada.
          message.error(err?.response?.data?.message || 'Error al procesar en Gescom');
        } finally {
          setProcesando(false);
        }
      },
    });
  };

  const handleEstadoChange = (value: string | number) => {
    setSelectedEstado(Number(value));
    setSearchText('');
    setSelectedRowKeys([]);
    setBatchEstado(undefined);
  };

  const handleRefresh = async () => {
    await Promise.all([loadData(), loadCounts()]);
  };

  // Estados aplicables en lote (se omiten Pendiente y Traslado OK)
  const BATCH_ESTADOS = ESTADOS_TRASLADO.filter(e => e.value === 2 || e.value === 3 || e.value === 5);

  const doBatchApply = async () => {
    setBatchLoading(true);
    try {
      await Promise.all(selectedRowKeys.map(id => updateTrasladoEstado(Number(id), batchEstado!)));
      message.success(`${selectedRowKeys.length} traslados actualizados`);
      setSelectedRowKeys([]);
      setBatchEstado(undefined);
      await Promise.all([loadData(), loadCounts()]);
    } catch {
      message.error('Error al actualizar los traslados en lote');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleBatchApply = () => {
    if (batchEstado === undefined || selectedRowKeys.length === 0) return;
    const estadoLabel = ESTADOS_TRASLADO.find(e => e.value === batchEstado)?.label || '';
    Modal.confirm({
      title: 'Confirmar cambio de estado',
      content: `Se marcarán ${selectedRowKeys.length} traslados como "${estadoLabel}". ¿Continuar?`,
      okText: 'Aceptar',
      cancelText: 'Cancelar',
      onOk: doBatchApply,
    });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  };

  const columns: ColumnsType<ReconciliacionTraslado> = [
    {
      title: 'Rec',
      dataIndex: 'reconciliacionId',
      key: 'rec',
      className: 'fct-num',
      align: 'right',
      width: 45,
      render: (val: number) => <Text type="secondary" style={{ fontSize: 12 }}>{val}</Text>,
    },
    {
      title: 'Farmacia',
      key: 'farmacia',
      render: (_, r) => r.farmaciaGescom?._Name || `CU2 ID ${r.idFarmaciaCU2}`,
      ellipsis: true,
      width: 260,
    },
    {
      title: 'Provincia',
      dataIndex: 'provincia',
      key: 'provincia',
      width: 130,
    },
    {
      title: 'Dir. anterior',
      key: 'dirAnterior',
      render: (_, r) => `${r.direccionAnterior || ''}, ${r.localidadAnterior || ''}`.replace(/^, |, $/g, '') || '-',
      ellipsis: true,
      width: 220,
    },
    {
      title: 'Dir. nueva',
      key: 'dirNueva',
      render: (_, r) => `${r.direccion || ''}, ${r.localidad || ''}`.replace(/^, |, $/g, '') || '-',
      ellipsis: true,
      width: 220,
    },
    {
      title: 'Fecha',
      dataIndex: 'fechaCreacion',
      key: 'fecha',
      className: 'fct-num',
      align: 'right',
      width: 110,
      render: (val: string) => new Date(val).toLocaleDateString('es-ES'),
    },
    {
      title: 'Estado',
      dataIndex: 'estadoId',
      key: 'estado',
      width: 100,
      render: (val: number) => <EstadoTraslado estadoId={val} />,
    },
    {
      title: '',
      key: 'notas',
      width: 40,
      render: (_, r) => r.notasGestion ? (
        <Tooltip title={r.notasGestion}><FileTextOutlined /></Tooltip>
      ) : null,
    },
  ];

  if (error) {
    return (
      <Result
        status="error"
        title="Error al cargar traslados"
        extra={<Button type="primary" onClick={loadData} icon={<ReloadOutlined />}>Reintentar</Button>}
      />
    );
  }

  return (
    <div>
      {/* Cabecera */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 16 }}>
        <div style={{ flex: 1 }} />
        <Input
          placeholder="Buscar farmacia..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => handleSearch(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
        <Segmented
          value={selectedEstado}
          onChange={handleEstadoChange}
          disabled={loading || loadingCounts}
          size="large"
          options={[
            {
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>Todos</span>
                  <Badge
                    count={Object.values(estadoCounts).reduce((a, b) => a + b, 0)}
                    style={{ backgroundColor: fct.tinta['700'] }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: ESTADO_TODOS,
            },
            ...ESTADOS_TRASLADO.map(e => ({
              label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{e.label}</span>
                  <Badge
                    count={estadoCounts[e.value] || 0}
                    style={{ backgroundColor: e.badgeColor }}
                    showZero
                    overflowCount={9999}
                  />
                </div>
              ),
              value: e.value,
            })),
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>Actualizar</Button>
      </div>

      {/* Barra de tratamiento por lotes (solo en pendientes y con selección) */}
      {selectedEstado === ESTADO_PENDIENTE && selectedRowKeys.length > 0 && (
        <div style={{
          marginBottom: 12, padding: '10px 16px',
          background: fct.estado.proceso.relleno, border: `1px solid ${fct.estado.proceso.linea}`, borderRadius: fct.radio.control,
          display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <Text strong><span className="fct-mono">{selectedRowKeys.length}</span> seleccionados</Text>
          <Select
            placeholder="Marcar como..."
            value={batchEstado}
            onChange={setBatchEstado}
            style={{ width: 200 }}
            options={BATCH_ESTADOS.map(e => ({
              value: e.value,
              label: <EstadoTraslado estadoId={e.value} />,
            }))}
          />
          <Button type="primary" disabled={batchEstado === undefined} loading={batchLoading} onClick={handleBatchApply}>
            Aceptar
          </Button>
          <Button onClick={() => { setSelectedRowKeys([]); setBatchEstado(undefined); }}>Cancelar</Button>
        </div>
      )}

      {/* Tabla */}
      <Table
        dataSource={filteredData.map(t => ({ ...t, key: t.id }))}
        columns={columns}
        rowSelection={selectedEstado === ESTADO_PENDIENTE ? rowSelection : undefined}
        loading={loading}
        pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: total => `${total} registros` }}
        onRow={record => ({
          onClick: () => openDetail(record),
          style: { cursor: 'pointer' },
        })}
        size="middle"
        scroll={{ x: 1000 }}
        locale={{ emptyText: <Empty description="No hay traslados" /> }}
      />

      {/* Modal de detalle con comparativa */}
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        title={selectedItem ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span><ShopOutlined style={{ marginRight: 8 }} />Farmacia. Dirección actual en Gescom:</span>
            {!selectedItem.idFarmaciaGS ? (
              <Text type="secondary" style={{ fontWeight: 'normal' }}>(sin vincular)</Text>
            ) : loadingGescom ? (
              <Spin size="small" />
            ) : (
              <span
                style={{
                  backgroundColor: fct.estado.exito.relleno,
                  border: `1px solid ${fct.estado.exito.linea}`,
                  color: fct.estado.exito.texto,
                  fontWeight: 600,
                  padding: '2px 10px',
                  borderRadius: fct.radio.micro,
                }}
              >
                {/* Sin enlace a Gedeco: el ID ya es de Gescom, que Gedeco no conoce (#149); el enlace nuevo, en #151. */}
                {[gescomFarmacia?.direccion, gescomFarmacia?.localidad].filter(Boolean).join(', ') || '—'}
              </span>
            )}
          </span>
        ) : ''}
        width={750}
      >
        {selectedItem && (
          <div>
            {/* Identificadores de la farmacia */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, alignItems: 'center' }}>
              <span className="fct-codigo">CU2 {selectedItem.idFarmaciaCU2}</span>
              {selectedItem.idFarmaciaGS ? (
                <span className="fct-codigo">Gescom {selectedItem.idFarmaciaGS}</span>
              ) : (
                <FctEstado personalizado={{ etiqueta: 'Gescom: sin vincular', familia: 'aviso' }} />
              )}
            </div>

            {/* Aviso: farmacia no vinculada */}
            {!selectedItem.idFarmaciaGS && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Farmacia no vinculada a Gescom"
                description="No se puede abrir la ficha del CRM ni consultar titulares hasta que la farmacia esté vinculada (idFarmaciaGS). Vincúlela antes de aplicar el traslado en el CRM."
              />
            )}

            {/* Comparativa de direcciones */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={11}>
                <Card
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Dropdown
                        menu={{
                          items: [{
                            key: 'gs',
                            label: <span>ID Gescom: <strong className="fct-mono">{selectedItem.idFarmaciaGS}</strong> <CopyOutlined style={{ marginLeft: 4 }} /></span>,
                            onClick: () => { navigator.clipboard.writeText(String(selectedItem.idFarmaciaGS || '')); },
                          }],
                        }}
                        trigger={['click']}
                      >
                        <span style={{ cursor: 'pointer' }}>
                          Dirección previa
                        </span>
                      </Dropdown>
                      <Tooltip title="Ver titulares actuales en Gescom">
                        <InfoCircleOutlined
                          aria-label="Ver titulares actuales en Gescom"
                          style={{ cursor: 'pointer', fontSize: 16 }}
                          onClick={() => setShowTitularesModal(true)}
                        />
                      </Tooltip>
                    </div>
                  }
                  size="small"
                  bordered
                  style={{ background: `linear-gradient(to bottom, ${fct.superficie.tarjeta} 0%, ${fct.superficie.hundida} 100%)`, boxShadow: fct.elev['1'], borderColor: fct.linea.tarjeta }}
                  headStyle={{ backgroundColor: fct.superficie.hundida, fontWeight: 'bold' }}
                >
                  <div>
                    <div><Text type="secondary" style={{ fontSize: 12 }}>Dirección</Text><div>{selectedItem.direccionAnterior || '-'}</div></div>
                    <div style={{ marginTop: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>CP</Text><div className="fct-mono">{selectedItem.cpAnterior || '-'}</div></div>
                    <div style={{ marginTop: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>Localidad</Text><div>{selectedItem.localidadAnterior || '-'}</div></div>
                    <div style={{ marginTop: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>Provincia</Text><div>{selectedItem.provinciaAnterior || '-'}</div></div>
                  </div>
                </Card>
              </Col>
              <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ArrowRightOutlined style={{ fontSize: 20 }} />
              </Col>
              <Col span={11}>
                <Card
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Nueva dirección</span>
                      <Text type="secondary" className="fct-mono" style={{ fontSize: 12, fontWeight: 'normal' }}>
                        {new Date(selectedItem.fechaCreacion).toLocaleDateString('es-ES')}
                      </Text>
                    </div>
                  }
                  size="small"
                  bordered
                  style={{ borderColor: fct.linea.tarjeta, boxShadow: fct.elev['1'] }}
                  headStyle={{ backgroundColor: fct.superficie.hundida, fontWeight: 'bold' }}
                >
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Dirección</Text>
                    <ValorNuevo cambiado={selectedItem.direccion !== selectedItem.direccionAnterior}>
                      {selectedItem.direccion || '-'}
                    </ValorNuevo>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>CP</Text>
                    <ValorNuevo cambiado={selectedItem.cp !== selectedItem.cpAnterior} mono>
                      {selectedItem.cp || '-'}
                    </ValorNuevo>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Localidad</Text>
                    <ValorNuevo cambiado={selectedItem.localidad !== selectedItem.localidadAnterior}>
                      {selectedItem.localidad || '-'}
                    </ValorNuevo>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Provincia</Text>
                    <ValorNuevo cambiado={selectedItem.provincia !== selectedItem.provinciaAnterior}>
                      {selectedItem.provincia || '-'}
                    </ValorNuevo>
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Comentario */}
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Comentario</Text>
            <TextArea
              value={editNotas}
              onChange={e => setEditNotas(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 5 }}
              placeholder="Añadir comentario..."
              style={{ marginBottom: 16 }}
            />

            {/* Panel de resultado del procesamiento */}
            {(procesarResult || selectedItem.notasProcesamiento) && (
              <Collapse
                size="small"
                defaultActiveKey={procesarResult ? ['1'] : []}
                style={{ marginBottom: 16 }}
                items={[{
                  key: '1',
                  label: (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span><ThunderboltOutlined style={{ marginRight: 8 }} />Resultado del procesamiento</span>
                      {selectedItem.fechaProcesamiento && <Text type="secondary" className="fct-mono" style={{ fontSize: 12 }}>{new Date(selectedItem.fechaProcesamiento).toLocaleDateString('es-ES')}</Text>}
                    </div>
                  ),
                  children: (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <ResultadoIcono
                        ok={procesarResult ? procesarResult.error === 0 : !selectedItem.notasProcesamiento?.startsWith('Error al procesar')}
                        style={{ marginTop: 4 }}
                      />
                      <Text style={{ whiteSpace: 'pre-wrap' }}>
                        {procesarResult
                          ? (procesarResult.error !== 0 ? procesarResult.mensajeError : (procesarResult.resultadoCasoLimpio || 'Dirección actualizada en Gescom'))
                          : selectedItem.notasProcesamiento}
                      </Text>
                    </div>
                  ),
                }]}
              />
            )}

            {/* Acciones: info + estado selector + Guardar + Procesar */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <Tooltip title="Guardar solo registra el estado de seguimiento. Procesar cambia la calle de la farmacia en Gescom y, si tiene éxito, marca el traslado como «Cambio nombre» y lo refleja en CU2.">
                <InfoCircleOutlined style={{ cursor: 'pointer', fontSize: 16 }} />
              </Tooltip>
              <Select
                value={editEstadoId}
                onChange={setEditEstadoId}
                style={{ width: 180 }}
              >
                {ESTADOS_TRASLADO.map(e => (
                  <Select.Option key={e.value} value={e.value}>
                    <EstadoTraslado estadoId={e.value} />
                  </Select.Option>
                ))}
              </Select>
              <Button
                icon={<SaveOutlined />}
                loading={actionLoading}
                onClick={() => handleUpdateEstado(selectedItem.id, editEstadoId, editNotas || undefined)}
              >
                Guardar
              </Button>
              {selectedItem.estadoId === ESTADO_PENDIENTE && (
                <Tooltip title={!selectedItem.idFarmaciaGS ? 'La farmacia no está vinculada a Gescom' : undefined}>
                  <Button
                    type="primary"
                    icon={<ThunderboltOutlined />}
                    loading={procesando}
                    onClick={() => handleProcesar(selectedItem)}
                    disabled={!selectedItem.idFarmaciaGS}
                  >
                    Procesar...
                  </Button>
                </Tooltip>
              )}
              <div style={{ marginLeft: 'auto' }}>
                <AbrirCasoButton
                  prefill={{
                    entidadTipo: 'Traslado',
                    entidadRefId: selectedItem.id,
                    reconciliacionId: selectedItem.reconciliacionId,
                    titulo: `Revisión traslado #${selectedItem.id}`,
                    descripcion:
                      `Farmacia: ${selectedItem.farmaciaGescom?._Name ?? '—'}\n` +
                      `id_farmacia (CU2): ${selectedItem.idFarmaciaCU2}\n` +
                      `id_farmacia_gs (Gescom): ${selectedItem.idFarmaciaGS ?? '—'}`,
                  }}
                />
              </div>
            </div>
          </div>
        )}
        {/* Titulares o empleados actuales en Gescom: dentro del detalle, para que Ant Design la ponga siempre
            por encima de él, aunque el detalle se monte después (#209). */}
        {selectedItem && (
          <FarmaciaTitularesModal
            visible={showTitularesModal}
            onClose={() => setShowTitularesModal(false)}
            farmaciaId={selectedItem.idFarmaciaGS || 0}
            farmaciaName={selectedItem.farmaciaGescom?._Name || 'Sin datos'}
            userId={1}
            cargarTitulares={getRecTitularesFarmaciaGescom}
          />
        )}
      </Modal>
    </div>
  );
};

export default TrasladosTab;
